import { useState, useCallback } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// Initialize API client with empty baseUrl since paths already include /api/v1.
// 实验室端点都受 RBAC + 租户隔离 + 回输身份核对约束保护；typed client 必须
// 通过共享的 tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

interface ApiError {
  message: string;
  code?: string;
}

function friendlyMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    if (err.status === 401) return '会话已失效，请重新登录后再试';
    if (err.status === 403) return '当前角色没有实验室模块访问权限';
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

// Type aliases for OpenAPI DTOs
type CellBatchDto = ApiComponents['schemas']['CellBatchDto'];
type ExceptionEventDto = ApiComponents['schemas']['ExceptionEventDto'];

// View models - map from DTO to UI representation
export interface CellBatchView {
  id: string;
  batchNo: string; // Maps from DTO.batchNo
  status: string;
  version: number;
  customerId: string; // Customer ID instead of patient name/id (PII protection)
  patientNameHash?: string; // Hash only, not plaintext
  patientIdCardHash?: string; // Hash only, not plaintext
  createdAt: string;
  updatedAt: string;
}

export interface ExceptionEventView {
  id: string;
  category: string;
  severity: string;
  status: string;
  title: string;
  description: string;
  cellBatchId?: string;
  infusionOrderId?: string;
  reportedBy: string;
  reportedAt: string;
  resolvedBy?: string;
  resolvedAt?: string;
  version?: number; // Provided by backend for optimistic locking, defaults to 0 if not available
}

// Mapper functions
function mapCellBatchDtoToView(dto: CellBatchDto): CellBatchView {
  return {
    id: dto.id,
    batchNo: dto.batchNo,
    status: dto.status,
    version: dto.version,
    customerId: dto.customerId,
    patientNameHash: dto.patientNameHash ?? undefined,
    patientIdCardHash: dto.patientIdCardHash ?? undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

function mapExceptionEventDtoToView(dto: ExceptionEventDto): ExceptionEventView {
  return {
    id: dto.id,
    category: dto.category,
    severity: dto.severity,
    status: dto.status,
    title: dto.title,
    description: dto.description,
    cellBatchId: dto.cellBatchId ?? undefined,
    infusionOrderId: dto.infusionOrderId ?? undefined,
    reportedBy: dto.reportedBy,
    reportedAt: dto.reportedAt,
    resolvedBy: dto.resolvedBy ?? undefined,
    resolvedAt: dto.resolvedAt ?? undefined,
    version: 0, // Default version for optimistic locking; should be updated by backend if available
  };
}

// ============================================================================
// Cell Batches Hooks
// ============================================================================

/**
 * Fetch cell batches list with pagination and filtering.
 */
export function useCellBatchesList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(
    async (params?: { page?: number; limit?: number; status?: 'planned' | 'sample_collected' | 'sample_received' | 'processing' | 'qc_pending' | 'released' | 'infusion_scheduled' | 'infused' | 'archived' | 'exception' }) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.GET(
          '/api/v1/lab/cell-batches',
          {
            params: {
              query: {
                page: params?.page ?? 1,
                limit: params?.limit ?? 20,
                ...(params?.status && { status: params.status }),
              },
            },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to fetch cell batches'
          );
        }

        // Map response to view model
        const items = Array.isArray(data)
          ? data.map(dto => mapCellBatchDtoToView(dto))
          : [];

        return {
          items,
          total: items.length,
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
        };
      } catch (err) {
        const apiErr: ApiError = {
          message: friendlyMessage(err, 'Failed to fetch cell batches'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { fetch, loading, error };
}

/**
 * Fetch single cell batch detail.
 */
export function useCellBatchDetail(batchId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await apiClient.GET(
        '/api/v1/lab/cell-batches/{id}',
        { params: { path: { id: batchId } } }
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || 'Failed to fetch cell batch'
        );
      }

      return data ? mapCellBatchDtoToView(data) : null;
    } catch (err) {
      const apiErr: ApiError = {
        message: friendlyMessage(err, 'Failed to fetch cell batch'),
      };
      setError(apiErr);
      throw apiErr;
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  return { fetch, loading, error };
}

/**
 * Transition cell batch status.
 * Uses nextStatus and expectedVersion per OpenAPI spec.
 */
export function useCellBatchTransition(batchId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const transition = useCallback(
    async (nextStatus: 'planned' | 'sample_collected' | 'sample_received' | 'processing' | 'qc_pending' | 'released' | 'infusion_scheduled' | 'infused' | 'archived' | 'exception', expectedVersion: number) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.PUT(
          '/api/v1/lab/cell-batches/{id}/status',
          {
            params: { path: { id: batchId } },
            body: { nextStatus, expectedVersion },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message ||
              'Failed to transition batch status'
          );
        }

        return data ? mapCellBatchDtoToView(data) : null;
      } catch (err) {
        const apiErr: ApiError = {
          message:
            friendlyMessage(err, 'Failed to transition batch status'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [batchId]
  );

  return { transition, loading, error };
}

// ============================================================================
// Exceptions Hooks
// ============================================================================

/**
 * Fetch exceptions list with severity filtering.
 */
export function useExceptionsList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      severity?: 'low' | 'medium' | 'high' | 'critical';
      cellBatchId?: string;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.GET(
          '/api/v1/lab/exceptions',
          {
            params: {
              query: {
                page: params?.page ?? 1,
                limit: params?.limit ?? 20,
                ...(params?.severity && { severity: params.severity }),
                ...(params?.cellBatchId && { cellBatchId: params.cellBatchId }),
              },
            },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to fetch exceptions'
          );
        }

        // Map response to view model
        const items = Array.isArray(data)
          ? data.map(dto => mapExceptionEventDtoToView(dto))
          : [];

        return {
          items,
          total: items.length,
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
        };
      } catch (err) {
        const apiErr: ApiError = {
          message: friendlyMessage(err, 'Failed to fetch exceptions'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { fetch, loading, error };
}

// ============================================================================
// Infusion Identity Verification Hook
// ============================================================================

/**
 * Infusion identity verification (medical safety red line).
 * 执行人与审核人必须不同，后端拒绝必须如实显示。
 */
export function useInfusionIdentityVerify() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const verify = useCallback(
    async (payload: {
      infusionId: string;
      reviewerId: string;
      enteredName: string;
      enteredIdCardNumber: string;
      expectedInfusionVersion: number;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.POST(
          '/api/v1/lab/infusions/{id}/verify',
          {
            params: { path: { id: payload.infusionId } },
            body: {
              enteredName: payload.enteredName,
              enteredIdCardNumber: payload.enteredIdCardNumber,
              reviewerUserId: payload.reviewerId,
              method: 'manual_two_person' as const,
              expectedInfusionVersion: payload.expectedInfusionVersion,
            },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message ||
              'Failed to verify infusion identity'
          );
        }

        return data || null;
      } catch (err) {
        const apiErr: ApiError = {
          message:
            friendlyMessage(err, 'Failed to verify infusion identity'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { verify, loading, error };
}

// ============================================================================
// Release Orders Hooks
// ============================================================================

/**
 * Release order DTO and view model types.
 */
type ReleaseOrderDto = ApiComponents['schemas']['ReleaseOrderDto'];

export interface ReleaseOrderView {
  id: string;
  cellBatchId: string;
  releaseNo: string;
  status: string; // pending | approved | rejected | recalled
  version: number;
  createdAt: string;
}

function mapReleaseOrderDtoToView(dto: ReleaseOrderDto): ReleaseOrderView {
  return {
    id: dto.id,
    cellBatchId: dto.cellBatchId,
    releaseNo: dto.releaseNo,
    status: dto.status,
    version: dto.version,
    createdAt: dto.createdAt,
  };
}

/**
 * Fetch release orders list with pagination and filtering.
 */
export function useReleaseOrdersList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      status?: 'pending' | 'approved' | 'rejected' | 'recalled';
      cellBatchId?: string;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.GET(
          '/api/v1/lab/release-orders',
          {
            params: {
              query: {
                page: params?.page ?? 1,
                limit: params?.limit ?? 20,
                ...(params?.status && { status: params.status }),
                ...(params?.cellBatchId && { cellBatchId: params.cellBatchId }),
              },
            },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to fetch release orders'
          );
        }

        const items = Array.isArray(data)
          ? data.map(dto => mapReleaseOrderDtoToView(dto))
          : [];

        return {
          items,
          total: items.length,
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
        };
      } catch (err) {
        const apiErr: ApiError = {
          message: friendlyMessage(err, 'Failed to fetch release orders'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { fetch, loading, error };
}

/**
 * Fetch single release order detail.
 */
export function useReleaseOrderDetail(releaseOrderId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await apiClient.GET(
        '/api/v1/lab/release-orders/{id}',
        { params: { path: { id: releaseOrderId } } }
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || 'Failed to fetch release order'
        );
      }

      return data ? mapReleaseOrderDtoToView(data) : null;
    } catch (err) {
      const apiErr: ApiError = {
        message: friendlyMessage(err, 'Failed to fetch release order'),
      };
      setError(apiErr);
      throw apiErr;
    } finally {
      setLoading(false);
    }
  }, [releaseOrderId]);

  return { fetch, loading, error };
}

/**
 * Transition release order status.
 * Uses nextStatus and expectedVersion per OpenAPI spec.
 */
export function useReleaseOrderTransition(releaseOrderId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const transition = useCallback(
    async (nextStatus: 'pending' | 'approved' | 'rejected' | 'recalled', expectedVersion: number) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.POST(
          '/api/v1/lab/release-orders/{id}/status',
          {
            params: { path: { id: releaseOrderId } },
            body: { nextStatus, expectedVersion },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message ||
              'Failed to transition release order status'
          );
        }

        return data ? mapReleaseOrderDtoToView(data) : null;
      } catch (err) {
        const apiErr: ApiError = {
          message:
            friendlyMessage(err, 'Failed to transition release order status'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [releaseOrderId]
  );

  return { transition, loading, error };
}

// ============================================================================
// QC Orders Hooks
// ============================================================================

/**
 * QC order DTO and view model types.
 */
type QcOrderDto = ApiComponents['schemas']['QcOrderDto'];

export interface QcOrderView {
  id: string;
  cellBatchId: string;
  qcNo: string;
  status: string; // pending | in_progress | passed | failed | retest_required | cancelled
  resultPayload?: Record<string, unknown>;
  version: number;
  createdAt: string;
}

function mapQcOrderDtoToView(dto: QcOrderDto): QcOrderView {
  return {
    id: dto.id,
    cellBatchId: dto.cellBatchId,
    qcNo: dto.qcNo,
    status: dto.status,
    resultPayload: dto.resultPayload ?? undefined,
    version: dto.version,
    createdAt: dto.createdAt,
  };
}

/**
 * Fetch QC orders list with pagination and filtering.
 */
export function useQcOrdersList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      status?: 'pending' | 'in_progress' | 'passed' | 'failed' | 'retest_required' | 'cancelled';
      cellBatchId?: string;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.GET(
          '/api/v1/lab/qc-orders',
          {
            params: {
              query: {
                page: params?.page ?? 1,
                limit: params?.limit ?? 20,
                ...(params?.status && { status: params.status }),
                ...(params?.cellBatchId && { cellBatchId: params.cellBatchId }),
              },
            },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to fetch QC orders'
          );
        }

        const items = Array.isArray(data)
          ? data.map(dto => mapQcOrderDtoToView(dto))
          : [];

        return {
          items,
          total: items.length,
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
        };
      } catch (err) {
        const apiErr: ApiError = {
          message: friendlyMessage(err, 'Failed to fetch QC orders'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { fetch, loading, error };
}

/**
 * Fetch single QC order detail.
 */
export function useQcOrderDetail(qcOrderId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await apiClient.GET(
        '/api/v1/lab/qc-orders/{id}',
        { params: { path: { id: qcOrderId } } }
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || 'Failed to fetch QC order'
        );
      }

      return data ? mapQcOrderDtoToView(data) : null;
    } catch (err) {
      const apiErr: ApiError = {
        message: friendlyMessage(err, 'Failed to fetch QC order'),
      };
      setError(apiErr);
      throw apiErr;
    } finally {
      setLoading(false);
    }
  }, [qcOrderId]);

  return { fetch, loading, error };
}

/**
 * Transition QC order status.
 * Uses nextStatus and expectedVersion per OpenAPI spec.
 */
export function useQcOrderTransition(qcOrderId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const transition = useCallback(
    async (nextStatus: 'pending' | 'in_progress' | 'passed' | 'failed' | 'retest_required' | 'cancelled', expectedVersion: number) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.POST(
          '/api/v1/lab/qc-orders/{id}/status',
          {
            params: { path: { id: qcOrderId } },
            body: { nextStatus, expectedVersion },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message ||
              'Failed to transition QC order status'
          );
        }

        return data ? mapQcOrderDtoToView(data) : null;
      } catch (err) {
        const apiErr: ApiError = {
          message:
            friendlyMessage(err, 'Failed to transition QC order status'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [qcOrderId]
  );

  return { transition, loading, error };
}

// ============================================================================
// Infusion Orders Hooks
// ============================================================================

/**
 * Infusion order DTO and view model types.
 */
type InfusionOrderDto = ApiComponents['schemas']['InfusionOrderDto'];

export interface InfusionOrderView {
  id: string;
  infusionNo: string;
  cellBatchId: string;
  customerId: string;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

function mapInfusionOrderDtoToView(dto: InfusionOrderDto): InfusionOrderView {
  return {
    id: dto.id,
    infusionNo: dto.infusionNo,
    cellBatchId: dto.cellBatchId,
    customerId: dto.customerId,
    status: dto.status,
    version: dto.version,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

/**
 * Fetch infusion orders list with pagination and filtering.
 */
export function useInfusionOrdersList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      status?: string;
      customerId?: string;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.GET(
          '/api/v1/lab/infusions',
          {
            params: {
              query: {
                page: params?.page ?? 1,
                limit: params?.limit ?? 20,
                ...(params?.status && { status: params.status }),
                ...(params?.customerId && { customerId: params.customerId }),
              },
            },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to fetch infusion orders'
          );
        }

        const items = Array.isArray(data)
          ? data.map(dto => mapInfusionOrderDtoToView(dto))
          : [];

        return {
          items,
          total: items.length,
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
        };
      } catch (err) {
        const apiErr: ApiError = {
          message: friendlyMessage(err, 'Failed to fetch infusion orders'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { fetch, loading, error };
}

/**
 * Fetch single infusion order detail.
 */
export function useInfusionOrderDetail(infusionOrderId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await apiClient.GET(
        '/api/v1/lab/infusions/{id}',
        { params: { path: { id: infusionOrderId } } }
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || 'Failed to fetch infusion order'
        );
      }

      return data ? mapInfusionOrderDtoToView(data) : null;
    } catch (err) {
      const apiErr: ApiError = {
        message: friendlyMessage(err, 'Failed to fetch infusion order'),
      };
      setError(apiErr);
      throw apiErr;
    } finally {
      setLoading(false);
    }
  }, [infusionOrderId]);

  return { fetch, loading, error };
}

/**
 * Transition infusion order status.
 * Uses nextStatus and expectedVersion per OpenAPI spec.
 */
export function useInfusionOrderTransition(infusionOrderId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const transition = useCallback(
    async (nextStatus: string, expectedVersion: number) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.POST(
          '/api/v1/lab/infusions/{id}/status',
          {
            params: { path: { id: infusionOrderId } },
            body: { nextStatus, expectedVersion },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message ||
              'Failed to transition infusion order status'
          );
        }

        return data ? mapInfusionOrderDtoToView(data) : null;
      } catch (err) {
        const apiErr: ApiError = {
          message:
            friendlyMessage(err, 'Failed to transition infusion order status'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [infusionOrderId]
  );

  return { transition, loading, error };
}

// ============================================================================
// Exception Events Hooks
// ============================================================================

/**
 * Fetch single exception event detail.
 */
export function useExceptionEventDetail(eventId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await apiClient.GET(
        '/api/v1/lab/exceptions/{id}',
        { params: { path: { id: eventId } } }
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || 'Failed to fetch exception event'
        );
      }

      return data ? mapExceptionEventDtoToView(data) : null;
    } catch (err) {
      const apiErr: ApiError = {
        message: friendlyMessage(err, 'Failed to fetch exception event'),
      };
      setError(apiErr);
      throw apiErr;
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  return { fetch, loading, error };
}

/**
 * Resolve exception event.
 * Uses nextStatus and expectedVersion per OpenAPI spec.
 */
export function useExceptionEventResolve(eventId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const resolve = useCallback(
    async (nextStatus: string, expectedVersion: number) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.POST(
          '/api/v1/lab/exceptions/{id}/status',
          {
            params: { path: { id: eventId } },
            body: { nextStatus, expectedVersion },
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message ||
              'Failed to resolve exception event'
          );
        }

        return data ? mapExceptionEventDtoToView(data) : null;
      } catch (err) {
        const apiErr: ApiError = {
          message:
            friendlyMessage(err, 'Failed to resolve exception event'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [eventId]
  );

  return { resolve, loading, error };
}

// ============================================================================
// Barcode Scan Hook
// ============================================================================

/**
 * Barcode scan (辅助定位，不能替代身份核对).
 * Uses rawCode and source per OpenAPI spec.
 */
export function useBarcodeScan() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const scan = useCallback(async (rawCode: string, source?: string) => {
    setLoading(true);
    setError(null);

    try {
      const scanSource: 'scanner_gun' | 'mobile_camera' | 'manual_input' = source === 'scanner_gun' || source === 'mobile_camera' ? source : 'manual_input';
      const { data, error: apiError } = await apiClient.POST(
        '/api/v1/lab/scan',
        {
          body: {
            rawCode,
            source: scanSource,
          },
        }
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || 'Failed to scan barcode'
        );
      }

      return data || null;
    } catch (err) {
      const apiErr: ApiError = {
        message: friendlyMessage(err, 'Failed to scan barcode'),
      };
      setError(apiErr);
      throw apiErr;
    } finally {
      setLoading(false);
    }
  }, []);

  return { scan, loading, error };
}


// ============================================================================
// Sample Records Hooks
// ============================================================================

type SampleRecordDto = ApiComponents['schemas']['SampleRecordDto'];

export interface SampleRecordView {
  id: string;
  customerId: string;
  cellBatchId?: string;
  sampleNo: string;
  sampleType: string;
  status: 'collected' | 'received' | 'rejected' | 'retained' | 'discarded';
  collectedAmount?: string;
  collectedUnit?: string;
  version: number;
}

function mapSampleRecordDtoToView(dto: SampleRecordDto): SampleRecordView {
  return {
    id: dto.id,
    customerId: dto.customerId,
    cellBatchId: dto.cellBatchId ?? undefined,
    sampleNo: dto.sampleNo,
    sampleType: dto.sampleType,
    status: dto.status,
    collectedAmount: dto.collectedAmount ?? undefined,
    collectedUnit: dto.collectedUnit ?? undefined,
    version: dto.version,
  };
}

export function useSampleRecordsList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(
    async (params?: { page?: number; limit?: number; status?: 'collected' | 'received' | 'rejected' | 'retained' | 'discarded'; sampleNo?: string }) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await apiClient.GET('/api/v1/lab/sample-records', {
          params: {
            query: {
              page: params?.page || 1,
              limit: params?.limit || 20,
              status: params?.status,
              sampleNo: params?.sampleNo,
            },
          },
        });

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to fetch sample records'
          );
        }

        return data
          ? {
              items: (data as { items?: SampleRecordDto[]; total?: number }).items?.map((item: SampleRecordDto) =>
                mapSampleRecordDtoToView(item)
              ) || [],
              total: (data as { items?: SampleRecordDto[]; total?: number }).total || 0,
            }
          : null;
      } catch (err) {
        const apiErr: ApiError = {
          message:
            friendlyMessage(err, 'Failed to fetch sample records'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { fetch, loading, error };
}

export function useSampleRecordDetail(recordId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await apiClient.GET(
        '/api/v1/lab/sample-records/{id}',
        {
          params: { path: { id: recordId } },
        }
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || 'Failed to fetch sample record'
        );
      }

      return data ? mapSampleRecordDtoToView(data as SampleRecordDto) : null;
    } catch (err) {
      const apiErr: ApiError = {
        message: friendlyMessage(err, 'Failed to fetch sample record'),
      };
      setError(apiErr);
      throw apiErr;
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  return { fetch, loading, error };
}

export function useSampleRecordCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(
    async (data: {
      customerId: string;
      cellBatchId?: string;
      sampleNo: string;
      sampleType: string;
      collectedAmount?: string;
      collectedUnit: string;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const { data: responseData, error: apiError } = await apiClient.POST(
          '/api/v1/lab/sample-records',
          {
            body: data,
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to create sample record'
          );
        }

        return responseData ? mapSampleRecordDtoToView(responseData as SampleRecordDto) : null;
      } catch (err) {
        const apiErr: ApiError = {
          message: friendlyMessage(err, 'Failed to create sample record'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { create, loading, error };
}

export function useSampleRecordUpdate(recordId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const update = useCallback(
    async (data: {
      sampleNo: string;
      sampleType: string;
      status: string;
      collectedAmount?: string;
      collectedUnit: string;
      expectedVersion: number;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const { data: responseData, error: apiError } = await apiClient.PUT(
          '/api/v1/lab/sample-records/{id}',
          {
            params: { path: { id: recordId } },
            body: data,
          }
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || 'Failed to update sample record'
          );
        }

        return responseData ? mapSampleRecordDtoToView(responseData as SampleRecordDto) : null;
      } catch (err) {
        const apiErr: ApiError = {
          message: friendlyMessage(err, 'Failed to update sample record'),
        };
        setError(apiErr);
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [recordId]
  );

  return { update, loading, error };
}

// ============================================================================
// List-style transition hooks (Issue #345)
// ----------------------------------------------------------------------------
// The existing transition hooks above bind the resource id at hook
// construction, which is convenient for detail pages but awkward for list
// pages that need to fire transitions across many rows. The hooks below
// expose the same backend endpoints with id passed per call so a single
// AdminDataTable rowActions surface can drive non-physical removal
// (cancel / recall / abort / resolve) without remounting hooks per row.
// ============================================================================

export type QcOrderStatusNext =
  | 'pending'
  | 'in_progress'
  | 'passed'
  | 'failed'
  | 'retest_required'
  | 'cancelled';

export function useQcOrderTransitionById() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const transition = useCallback(
    async (
      id: string,
      nextStatus: QcOrderStatusNext,
      expectedVersion: number,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/lab/qc-orders/{id}/status',
          {
            params: { path: { id } },
            body: { nextStatus, expectedVersion },
          },
        );
        if (apiErr) {
          setError({
            message:
              (apiErr as { message?: string }).message ?? '质检单状态变更失败',
          });
          return false;
        }
        return true;
      } catch (err) {
        setError({ message: friendlyMessage(err, '质检单状态变更失败') });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { transition, loading, error };
}

export type ReleaseOrderStatusNext =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'recalled';

export function useReleaseOrderTransitionById() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const transition = useCallback(
    async (
      id: string,
      nextStatus: ReleaseOrderStatusNext,
      expectedVersion: number,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/lab/release-orders/{id}/status',
          {
            params: { path: { id } },
            body: { nextStatus, expectedVersion },
          },
        );
        if (apiErr) {
          setError({
            message:
              (apiErr as { message?: string }).message ?? '放行单状态变更失败',
          });
          return false;
        }
        return true;
      } catch (err) {
        setError({ message: friendlyMessage(err, '放行单状态变更失败') });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { transition, loading, error };
}

export interface InfusionTransitionInput {
  nextStatus: string;
  expectedVersion: number;
  abortReason?: string;
}

export function useInfusionOrderTransitionById() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const transition = useCallback(
    async (id: string, input: InfusionTransitionInput): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/lab/infusions/{id}/status',
          {
            params: { path: { id } },
            body: input,
          },
        );
        if (apiErr) {
          setError({
            message:
              (apiErr as { message?: string }).message ?? '回输单状态变更失败',
          });
          return false;
        }
        return true;
      } catch (err) {
        setError({ message: friendlyMessage(err, '回输单状态变更失败') });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { transition, loading, error };
}

export function useExceptionEventResolveById() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const resolve = useCallback(
    async (
      id: string,
      expectedVersion: number,
      resolutionNotes: string,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/lab/exceptions/{id}/resolve',
          {
            params: { path: { id } },
            body: { expectedVersion, resolutionNotes },
          },
        );
        if (apiErr) {
          setError({
            message:
              (apiErr as { message?: string }).message ?? '异常事件归档失败',
          });
          return false;
        }
        return true;
      } catch (err) {
        setError({ message: friendlyMessage(err, '异常事件归档失败') });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { resolve, loading, error };
}
